import { Page, Div, Button, Input, router, IRouteEvent } from '@codesuma/baseline'
import { PageHead } from '../../components/page-head'
import { Slides, WizardNav } from '../../components/wizard'
import calendar, { IDayCell } from '../../services/calendar'
import notes from '../../services/notes'
import settings from '../../services/settings'
import { dayLine } from '../../helpers/day-line'
import { paleYear } from '../../helpers/year'
import wizard from '../../components/wizard/index.module.css'
import style from './index.module.css'

// The note page (route /note?d=gy-gm-gd to write one, ?id=… to open one): the
// text and nothing else. Reached from the day's own list — the add line to
// write, tapping a note to change it — and saving returns there, where the day
// redraws.
//
// It is the only place a note is edited, the way /task is for a task and /event
// for an event. A note used to be a field sitting open on the day itself, which
// made a page of days look like a page of forms. A note is read far more often
// than it is written, so reading gets plain text and writing gets a page.
//
// One step, so the nav is a save button with nothing to step through. That is
// the point: a note is one question, and the page exists to give it room.
//
// One route for every day, like /day and /task: the date travels in the query,
// so the router caches a single page rather than one per date.

const todayCell = () => {
    const t = calendar.today()
    return calendar.dayAt(t.gy, t.gm, t.gd, 0)
}

export const NotePage = () => {
    const base = Page()
    // Inline so it wins over baseline's default page background and padding —
    // the calendar's own paper and ink, so this reads as the same surface as the
    // day it came from. The same shape the task page takes.
    base.style({
        background: 'var(--paper)',
        color: 'var(--ink)',
        padding: 'calc(var(--safe-top) + 1rem) var(--gutter) calc(var(--safe-bottom) + 1rem)',
    })
    base.addClass(style.base)

    let day: IDayCell = todayCell()
    let editing = '' // the id being changed, empty when writing a new one
    let text = ''

    // The app's one page head, with the line under it the editors carry.
    // Its date names the day being written about, so the page never loses
    // its context.
    const head = PageHead(undefined, { rule: true })
    base.append(head)

    const stage = Slides()
    const nav = WizardNav()
    base.append(stage, nav)

    // Only on an existing note, and only once confirmed: the first press arms
    // it, the second carries it out. The same delete the task page has, for the
    // same reason — cheaper than a dialog, and a stray tap cannot trigger it.
    const remove = Button()
    remove.addClass(style.remove)
    let armed = false
    const disarm = () => {
        armed = false
        remove.text('حذف یادداشت')
        remove.removeClass(style.removeArmed)
    }
    remove.on('click', () => {
        if (!armed) {
            armed = true
            remove.text('برای حذف دوباره بزنید')
            remove.addClass(style.removeArmed)
            return
        }
        notes.remove(editing)
        router.back()
    })
    base.append(remove)

    const renderNav = () => nav.setState({
        steps: 1, index: 0, nextLabel: 'ذخیره',
        nextEnabled: !!text.trim(), showPrev: false,
    })

    // The one step, and no question above it: the head already says which note
    // this is and which day it belongs to, and there is nothing else on the page
    // to tell the field apart from. Built on every entry, like every other
    // wizard's, so nothing of the last visit is left in it.
    const buildSlide = () => {
        const slide = Div()
        slide.addClass(wizard.body)

        // The placeholder names the two things people write here — what
        // happened, and what is worth keeping — which also says it takes either.
        const field = Input('خاطره‌ای یا نکته‌ای…', 'textarea')
        field.addClass(style.field)
        field.attr('dir', 'auto')
        field.setValue(text)
        field.on('input', ({ value }: { value: string }) => {
            text = value
            renderNav()
        })
        slide.append(field)

        return Object.assign(slide, { focus: () => field.focus() })
    }

    const save = () => {
        if (!text.trim()) return
        if (editing) notes.update(editing, text)
        else notes.add(day, text)
        router.back()
    }
    nav.on('next', save)

    // Every entry starts over — the router keeps this page alive between
    // visits, so neither the text nor the day may carry over.
    base.on('enter', ({ query }: IRouteEvent) => {
        const q = (query || {}) as Record<string, string>
        const existing = q.id ? notes.byId(q.id) : undefined

        if (existing) {
            editing = existing.id
            // The day is the one it was written for, not anything in the query.
            day = calendar.dayAt(existing.gy, existing.gm, existing.gd, 0)
            text = existing.text
        } else {
            editing = ''
            const parts = String(q.d || '').split('-').map(Number)
            day = parts.length === 3 && parts.every(n => Number.isFinite(n) && n > 0)
                ? calendar.dayAt(parts[0], parts[1], parts[2], 0)
                : todayCell()
            text = ''
        }

        head.setTitle(editing ? 'ویرایش یادداشت' : 'یادداشت تازه')
        head.setDate(paleYear(dayLine(calendar.dayAt(day.gy, day.gm, day.gd, 0), settings.get().system)))
        remove.style({ display: editing ? '' : 'none' })
        disarm()

        const slide = buildSlide()
        stage.show(slide, 'none')
        renderNav()
        // After the slide has settled, so the keyboard does not open onto a
        // moving field.
        setTimeout(() => slide.focus(), 220)
    })

    return base
}
